import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NumberInput from "../components/NumberInput";
import { useCart } from "../context/CartContext";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 330,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const CustomerCart = () => {
  const { cartItems, updateItemQuantity, removeItemFromCart, emptyCart } =
    useCart();
  const navigate = useNavigate();
  const [currentCartItems, setCurrentCartItems] = useState(cartItems);
  const storageUrl = process.env.REACT_APP_STORAGE_URL;
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const handleOpenConfirmationModal = () => setOpenConfirmationModal(true);
  const handleCloseConfirmationModal = () => setOpenConfirmationModal(false);
  useEffect(() => {
    setCurrentCartItems(cartItems);
  }, [cartItems]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    try {
      const updatedItems = currentCartItems.map((item) =>
        item.filter_id === itemId ? { ...item, quantity: newQuantity } : item
      );
      setCurrentCartItems(updatedItems);
      await updateItemQuantity(itemId, newQuantity);
    } catch (error) {
      console.error("Failed to update quantity", error.message);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      removeItemFromCart(itemId);
    } catch (error) {
      console.error("Failed to delete item", error.message);
    }
  };
  const sortedItems = useMemo(
    () => cartItems.sort((a, b) => a.index - b.index),
    [cartItems]
  );

  const handleEmptyCart = () => {
    emptyCart();
  };
  return (
    <Stack
      margin={"0"}
      direction="column"
      spacing={3}
      sx={{
        marginBottom: "60px",
        justifyContent: "center",
        alignItems: "stretch",
      }}
      overflow="auto"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Dine varer i kurven {/*Your Cart Items*/}
        </Typography>

        {sortedItems.length > 0 && (
          <Button
            sx={{
              marginLeft: "auto",
              right: 0,
              width: "100%",
              maxWidth: "120px",
              height: "40px",
              border: "1px solid #686868",
              borderRadius: "5px",
              flexShrink: 0,
              color: "#424242",
            }}
            onClick={handleOpenConfirmationModal}
          >
            Tøm kurv {/* Empty Cart */}
          </Button>
        )}
      </Box>

      {sortedItems.length === 0 ? (
        <Stack
          direction="column"
          spacing={3}
          sx={{
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Din indkøbskurv er tom. Prøv at tilføje nogle produkter.
            {/* Your cart is empty, Try to add some products.*/}{" "}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/Filters")}
            style={{
              width: "100%",
              maxWidth: "200px",
              height: "40px",
              backgroundColor: "#424242",
              borderRadius: "5px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              "&:hover": {
                backgroundColor: "#686868",
              },
              flexShrink: 0,
              margin: "10px",
              marginTop: 16,
            }}
          >
            Tilbage til shoppen {/* Go to Shop */}
          </Button>
        </Stack>
      ) : (
        <>
          {" "}
          <Box overflow="auto" direction="column" spacing={2} marginY={10}>
            {sortedItems.map((item) => (
              <Card
                key={item.id}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  height: "100%",
                  border: "1px solid rgba(66, 66, 66, 0.5)",
                  borderRadius: "10px",
                  overflow: "hidden",
                  boxSizing: "border-box",
                  paddingY: "5px",
                  marginX: "5px",
                  marginY: "8px",
                }}
                elevation={3}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    padding: "16px",
                    boxSizing: "border-box",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    display={"flex"}
                    flexDirection={"row"}
                    alignItems={"center"}
                    gap={"20px"}
                  >
                    <Box
                      sx={{
                        maxHeight: 50,
                        maxWidth: 50,
                        overflow: "hidden",
                        my: 2,
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={
                          item.filter.s_icon
                            ? `https://minio.jlfiltersystem.dk/uploads/${item.filter.s_icon}`
                            : "/images/filter_1836109.png"
                        }
                        alt={item.filter.s_name}
                        style={
                          item.filter.s_icon
                            ? { width: "100%", height: "auto" }
                            : { width: "80%", height: "auto" }
                        }
                      />{" "}
                    </Box>

                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        JL Number: {item.filter.jl_number}
                      </Typography>

                      <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Type: {item.filter.s_name}
                      </Typography>
                    </Box>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                      {item.filter.price === null ? (
                        <Typography variant="body2" color={"#FF9100"}>
                          <strong>Ring for pris</strong>
                        </Typography>
                      ) : item.filter.price?.Unit_Price ===
                        item.filter.prices?.Unit_Price_Discount ? (
                        <Typography variant="body1" color={"#00712D"}>
                          <strong>
                            {" "}
                            {item.filter.price?.Unit_Price.toFixed(2)} Dkk
                          </strong>
                        </Typography>
                      ) : (
                        <Typography variant="body1" color={"#00712D"}>
                          <strong>
                            {item.filter.price?.Unit_Price_Discount.toFixed(2)}{" "}
                            Dkk{" "}
                          </strong>
                        </Typography>
                      )}
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <NumberInput
                        value={item.quantity}
                        onChange={(e, newVal) => {
                          if (newVal > 99) return;
                          handleQuantityChange(item.filter_id, newVal);
                        }}
                        min={1}
                        max={99}
                        sx={{ flexShrink: 0 }}
                      />
                      <Box
                        sx={{
                          width: "47px",
                          height: "40px",
                          backgroundColor: "#424242",
                          borderRadius: "5px",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          "&:hover": {
                            backgroundColor: "#686868",
                          },
                          flexShrink: 0,
                        }}
                      >
                        <IconButton
                          aria-label="Delete Item"
                          size="large"
                          onClick={() => handleDeleteItem(item.filter_id)}
                        >
                          <DeleteOutlineIcon sx={{ color: "#ffffff" }} />
                        </IconButton>
                      </Box>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          ></Stack>
          <Modal
            open={openConfirmationModal}
            onClose={handleCloseConfirmationModal}
          >
            <Box sx={modalStyle}>
              <Typography
                id="modal-modal-title"
                variant="h5"
                component="h2"
                mb={3}
              >
                Bekræftelse{" "}
              </Typography>
              <Typography variant="p" my={3}>
                <strong>
                  <span>Er du sikker på at du vil tømme kurven ? </span>
                  <spane> </spane>
                  <span>Kan ikke fortrydes.</span>
                  Fortsæt ?{" "}
                </strong>
              </Typography>
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: "30px",
                }}
              >
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    handleEmptyCart();
                    handleCloseConfirmationModal();
                  }}
                  sx={{
                    width: "100%",

                    maxWidth: "150px",
                    height: "40px",
                    backgroundColor: "#424242",
                    borderRadius: "5px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                    margin: "10px",
                    marginTop: 2,
                    "&:hover": {
                      backgroundColor: "#686868",
                    },
                  }}
                >
                  Ja, tøm {/* Yes, Empty */}
                </Button>
                <Button
                  onClick={handleCloseConfirmationModal}
                  sx={{
                    width: "100%",
                    maxWidth: "150px",
                    height: "40px",
                    border: "1px solid #686868",
                    borderRadius: "5px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flexShrink: 0,
                    margin: "10px",
                    marginTop: 2,
                    color: "#424242",
                  }}
                >
                  Annuller{/* Cancel */}
                </Button>
              </Stack>
            </Box>
          </Modal>
        </>
      )}
    </Stack>
  );
};

export default CustomerCart;
